import * as sessionReplay from '@amplitude/session-replay-browser';
/* eslint-disable no-console */
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import { CLIENT_HEADERS, devEnv, isProduction } from '#src/config.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';

import { AnalyticsContext } from './index.ts';
import { sessionReplayState } from './sessionReplayState.ts';
import { maybeStartSessionReplay } from './startSessionReplay.ts';

type Props = {
  children: ReactNode;
};

declare global {
  interface Window {
    gtag: (
      type: string,
      event: string,
      options: { [key: string]: any }
    ) => void | undefined;
    analytics: {
      ready: (fn: () => void) => void;
      identify: (userId: string, traits?: { [trait: string]: any }) => void;
      track: (
        eventName: string,
        properties?: { [property: string]: any },
        options?: { [option: string]: any }
      ) => void;
      reset: () => void;
      user: () => {
        id: () => string;
        traits: (traits?: any) => any;
        anonymousId: () => string;
      };
      // From https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
      // > "if you pass only one string to page it is assumed to be name"
      page: (pageName: string | undefined) => void;
      addSourceMiddleware: (
        middleware: (options: {
          payload: any;
          next: (payload: any) => unknown;
          integrations: any;
        }) => void
      ) => void;
      load: () => void;
    };
  }
}

export const ETH_DEPOSITED_TRAIT = 'eth_deposited';
const ETH_WITHDRAWN_TRAIT = 'eth_withdrawn';
const ETH_TRANSFERRED_TRAIT = 'eth_transferred';

// By default segment analytics.js caches the user traits and sends them
// with each identify call. As some of amplitude traits have special
// properties like increment (trait is incremented by the sent value
// on each identify call).
const TRAITS_TO_RESET = [
  ETH_DEPOSITED_TRAIT,
  ETH_WITHDRAWN_TRAIT,
  ETH_TRANSFERRED_TRAIT,
];

const resetUserTraits = () => {
  if (!window.analytics) return;

  const { user } = window.analytics;
  if (user) {
    const traits = user().traits();
    TRAITS_TO_RESET.forEach(t => delete traits[t]);
    user().traits(traits);
  }
};

type IdentifyArgs = {
  userId?: string;
  traits?: { [trait: string]: any };
};

const identify = ({ userId, traits }: IdentifyArgs): void => {
  if (!window.analytics) return;

  const { user } = window.analytics;
  if (!userId && !user) {
    console.warn('Unable to identify without a userId');
    return;
  }

  const uuid = userId || user().id();

  window.analytics.identify(uuid, traits);
  if ((!isProduction || devEnv) && import.meta.env.MODE !== 'test') {
    console.log('🪲 Analytics User Identified: ', uuid, traits);
  }

  resetUserTraits();
};

export const EventsProvider = ({ children }: Props) => {
  const { isIosApp, isAndroidApp, postMessage } = useIsMobileApp();
  const {
    flags: { sessionReplayEnabled = false },
  } = useFeatureFlags();

  useEffect(
    () => () => {
      sessionReplay.shutdown();
      sessionReplayState.active = false;
    },
    []
  );

  const value = useMemo(() => {
    const requestClient = CLIENT_HEADERS['sorare-client'];
    const requestClientProperties = {
      request_client: requestClient,
      request_client_context: isIosApp
        ? 'ios'
        : isAndroidApp
          ? 'android'
          : requestClient,
      request_client_version: CLIENT_HEADERS['sorare-version'],
      request_client_build: CLIENT_HEADERS['sorare-build'],
    };
    return {
      identify: (id: string, traits?: { [trait: string]: any }) =>
        identify({ userId: id, traits }),
      track: (event: string, properties?: { [propery: string]: any }): void => {
        maybeStartSessionReplay(event, sessionReplayEnabled);

        const enrichedProperties = {
          ...requestClientProperties,
          ...properties,
        };

        if (window.analytics) {
          window.analytics.track(event, {
            ...sessionReplay.getSessionReplayProperties(),
            ...enrichedProperties,
          });
        }

        if (isAndroidApp) {
          postMessage('trackInAppEvent', {
            event,
            jsonProperties: JSON.stringify(enrichedProperties),
          });
        }

        if ((!isProduction || devEnv) && import.meta.env.MODE !== 'test') {
          console.log(
            '🪲 Analytics Event Tracked: "%s"',
            event,
            enrichedProperties
          );
        }
      },
    };
  }, [isIosApp, isAndroidApp, postMessage, sessionReplayEnabled]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
